import type {
  DocumentSectionSpec,
  DocumentTemplate,
} from '@/entities/templates';
import type { TemplateIssue } from '@/entities/templates';

import { isFieldListTemplate } from './fieldList';

// A field list's (a summary's, an evaluation's) titles, codes and AI
// instructions are checked here as scribe checks them, before a save or the
// start of a recording, as the model answers under each title. Codes are
// compared trimmed, titles trimmed and ignoring case, as scribe compares
// them.
export type StructureIssue =
  | { kind: 'duplicateTitle'; section: number }
  | { kind: 'emptyTitle'; section: number }
  | { kind: 'duplicateCode'; section: number }
  | { kind: 'longTitle'; section: number }
  | { kind: 'longInstruction'; section: number }
  | { kind: 'placeholder'; section: number };

// Scribe's limits on a field's title and AI instruction.
const FIELD_TITLE_MAX_LENGTH = 200;
const FIELD_INSTRUCTION_MAX_LENGTH = 4000;

const holdsPlaceholder = (text: string) => /\{\{.*?\}\}/s.test(text);

const findRepeated = (
  sections: DocumentSectionSpec[],
  read: (section: DocumentSectionSpec) => string,
) =>
  sections.flatMap((section, index) =>
    sections.some(
      (other, otherIndex) =>
        otherIndex !== index && read(other) === read(section),
    )
      ? [index]
      : [],
  );

const findFieldListIssues = (
  sections: DocumentSectionSpec[],
): StructureIssue[] => {
  const titled = sections.flatMap((section, index) =>
    section.Title.trim() ? [{ section, index }] : [],
  );
  return [
    ...sections.flatMap((section, index): StructureIssue[] =>
      section.Title.trim() ? [] : [{ kind: 'emptyTitle', section: index }],
    ),
    ...sections.flatMap((section, index): StructureIssue[] => [
      ...(section.Title.trim().length > FIELD_TITLE_MAX_LENGTH
        ? [{ kind: 'longTitle' as const, section: index }]
        : []),
      ...((section.Instructions ?? '').trim().length >
      FIELD_INSTRUCTION_MAX_LENGTH
        ? [{ kind: 'longInstruction' as const, section: index }]
        : []),
      ...(holdsPlaceholder(section.Title) ||
      holdsPlaceholder(section.Instructions ?? '')
        ? [{ kind: 'placeholder' as const, section: index }]
        : []),
    ]),
    ...findRepeated(sections, (section) => section.Code.trim()).map(
      (section): StructureIssue => ({ kind: 'duplicateCode', section }),
    ),
    ...findRepeated(
      titled.map(({ section }) => section),
      (section) => section.Title.trim().toLowerCase(),
    ).map(
      (position): StructureIssue => ({
        kind: 'duplicateTitle',
        section: titled[position].index,
      }),
    ),
  ];
};

// Block templates are checked by scribe alone, which returns its issues;
// templates with options are not edited here.
export const findStructureIssues = (
  template: DocumentTemplate,
): StructureIssue[] =>
  isFieldListTemplate(template) ? findFieldListIssues(template.sections) : [];

// A rejected thunk hands over its serialized error, so a scribe refusal is
// recognized by its name; only its text is written for the clinician.
export const getSaveRefusalText = (error: unknown) => {
  if (typeof error !== 'object' || error === null) {
    return undefined;
  }
  const { name, message } = error as { name?: unknown; message?: unknown };
  return name === 'ScribeError' && typeof message === 'string' && message.trim()
    ? message
    : undefined;
};

// A refused block template carries one issue per problem (kept through the
// version thunk's error); the editor marks each where it points.
export const getSaveRefusalIssues = (error: unknown): TemplateIssue[] => {
  const issues =
    typeof error === 'object' && error !== null
      ? (error as { issues?: unknown }).issues
      : undefined;
  return Array.isArray(issues) ? (issues as TemplateIssue[]) : [];
};

// Scribe joins the problems of a refused template with "; ".
export const splitSaveRefusal = (refusal: string) =>
  refusal
    .split('; ')
    .map((item) => item.trim())
    .filter(Boolean);

// Scribe writes where a problem is as `section 2 "Title" (code), line 4,
// block 1 (text): …`, counting from 1 in the template it was sent.
const ISSUE_POSITION =
  /^section (\d+) "(?:[^"\\]|\\.)*"(?: \([^)]*\))?(?:, line (\d+)(?:, block (\d+))?)?/;

const readIssuePosition = (message: string) => {
  const match = ISSUE_POSITION.exec(message);
  return match
    ? {
        section: Number(match[1]) - 1,
        line: match[2] ? Number(match[2]) - 1 : undefined,
        block: match[3] ? Number(match[3]) - 1 : undefined,
      }
    : undefined;
};

// A refused block template names its new lines and blocks by the ids scribe
// would have given them, which the editor does not know: those are found
// again by their place in the sections as they were sent.
export const locateTemplateIssues = ({
  issues,
  sent,
}: {
  issues: TemplateIssue[];
  sent: DocumentSectionSpec[];
}): TemplateIssue[] => {
  const ids = new Set(
    sent.flatMap((section) => [
      section.ID,
      ...section.Lines.flatMap((line) => [
        line.ID,
        ...line.Blocks.map((block) => block.ID),
      ]),
    ]),
  );
  const known = (id: string, found: string | undefined) =>
    !id || ids.has(id) ? id : found ?? '';
  return issues.map((issue) => {
    const position = readIssuePosition(issue.Message);
    const section = position ? sent[position.section] : undefined;
    const line =
      position?.line === undefined ? undefined : section?.Lines[position.line];
    const block =
      position?.block === undefined ? undefined : line?.Blocks[position.block];
    // A link names the block that opens its line, not one of the line's.
    const linkBlock = issue.Kind.startsWith('show_when_');
    return {
      ...issue,
      SectionID: known(issue.SectionID, section?.ID),
      LineID: known(issue.LineID, line?.ID),
      BlockID: known(issue.BlockID, linkBlock ? undefined : block?.ID),
    };
  });
};

export type TemplateIssuePlace = {
  sectionTitle: string;
  // Counted from 1 among the section's lines.
  line?: number;
  // The line, else the section, to show.
  targetId: string;
};

// Where an issue is in the template the editor shows.
export const findTemplateIssuePlace = (
  template: DocumentTemplate,
  issue: TemplateIssue,
): TemplateIssuePlace | undefined => {
  const lineId =
    issue.LineID ||
    template.sections
      .flatMap((section) => section.Lines)
      .find((line) => line.Blocks.some((block) => block.ID === issue.BlockID))
      ?.ID;
  const section = template.sections.find(
    (item) =>
      item.ID === issue.SectionID ||
      (!!lineId && item.Lines.some((line) => line.ID === lineId)),
  );
  if (!section) {
    return undefined;
  }
  const lineIndex = section.Lines.findIndex((line) => line.ID === lineId);
  return lineIndex < 0
    ? { sectionTitle: section.Title, targetId: section.ID }
    : {
        sectionTitle: section.Title,
        line: lineIndex + 1,
        targetId: section.Lines[lineIndex].ID,
      };
};
